import type { PaigeClient } from '@paige/api-client';
import type {
  ProblemActRequest,
  ProblemActResult,
  ProblemFilesPage,
  ProblemGroup,
  Problems,
  ProblemsQuery,
} from './types';
import { unwrap } from './http';

/**
 * The API keeps an answer for fifteen seconds, in each of its pods. Just after an action the screen asks for the
 * ledger as it is now, so what a person cleared is not shown back to them from another pod's kept answer.
 */
const FRESH_FOR_MS = 30_000;
let actedAt = 0;
const actedLately = (): boolean => Date.now() - actedAt < FRESH_FOR_MS;

/** How many of a row's files one read asks for (the screen's page). */
export const PROBLEM_FILES_A_PAGE = 25;

/** The admin Problems screen: the rows, one row's files, and what staff do about a row or some of its files. */
export function problemsApi(client: PaigeClient) {
  return {
    async getProblems(q: ProblemsQuery): Promise<Problems> {
      const query = {
        sort: q.sort,
        limit: q.limit,
        ...(q.company ? { company: q.company } : {}),
        ...(q.text ? { q: q.text } : {}),
        ...(q.reason ? { reason: q.reason } : {}),
        ...(actedLately() ? { fresh: true } : {}),
      };
      return unwrap(await client.GET('/admin/problems', { params: { query } })) as Problems;
    },
    async listProblemFiles(group: ProblemGroup, offset: number): Promise<ProblemFilesPage> {
      const query = {
        company: group.company.id,
        reason: group.reason,
        offset,
        limit: PROBLEM_FILES_A_PAGE,
      };
      const r = await client.GET('/admin/problems/files', { params: { query } });
      return unwrap(r) as ProblemFilesPage;
    },
    async actOnProblem(request: ProblemActRequest): Promise<ProblemActResult> {
      const body = {
        company: request.group.company.id,
        reason: request.group.reason,
        action: request.action,
        note: request.note,
        ...(request.files ? { files: request.files.map((f) => f.key) } : {}),
        ...(request.until ? { until: request.until } : {}),
      };
      const result = unwrap(await client.POST('/admin/problems/act', { body }));
      actedAt = Date.now();
      return result;
    },
  };
}

export type ProblemsApi = ReturnType<typeof problemsApi>;

/** Staff stopping work that is still running or waiting, from the Stages and queues drawer. */
export function workApi(client: PaigeClient) {
  return {
    /** Stop a batch, a document or a unit still running or waiting (`POST /admin/work/cancel`). */
    async stopWork(
      what: { kind: 'batch' | 'document' | 'unit'; id: string },
      note: string,
    ): Promise<{ units: number; documents: number }> {
      const result = unwrap(
        await client.POST('/admin/work/cancel', {
          body: { kind: what.kind, id: what.id, note, dryRun: false },
        }),
      );
      actedAt = Date.now();
      return { units: result.units, documents: result.documents };
    },
  };
}

export type WorkApi = ReturnType<typeof workApi>;
